import { PencilIcon, Trash2Icon, XSquareIcon } from "lucide-react";
import { useReducer } from "react";

import { deleteCategory } from "@/api/category";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MoonLoader } from "react-spinners";

import { useCategories } from "@/hooks/server-state/use-categories";

import AlertModal from "@/components/common/modals/alert-modal";
import CategoryModal from "@/components/common/modals/category-modal";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
  TableSearch
} from "@/components/ui/table";

enum ActionTypes {
  SET_CATEGORY = "SET_CATEGORY",
  SET_IS_MODAL_OPEN = "SET_IS_MODAL_OPEN",
  SET_IS_DELETE_MODAL_OPEN = "SET_IS_DELETE_MODAL_OPEN"
}

type Actions = {
  type: ActionTypes;
  payload?: any;
};

const initialState = {
  isDeleteModalOpen: false,
  isModalOpen: false,
  category: null
};

const categoryReducer = (state: typeof initialState, action: Actions) => {
  switch (action.type) {
    case ActionTypes.SET_CATEGORY:
      return { ...state, category: action.payload };
    case ActionTypes.SET_IS_MODAL_OPEN:
      return { ...state, isModalOpen: action.payload };
    case ActionTypes.SET_IS_DELETE_MODAL_OPEN:
      return { ...state, isDeleteModalOpen: action.payload };
    default:
      return state;
  }
};

const CategoriesPage = () => {
  const queryClient = useQueryClient();
  const { data: categories, isLoading } = useCategories();

  const { mutate, isPending } = useMutation({
    mutationFn: (categoryId: number) => deleteCategory(categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      dispatch({
        type: ActionTypes.SET_IS_DELETE_MODAL_OPEN,
        payload: false
      });
    }
  });

  const [category, dispatch] = useReducer(categoryReducer, initialState);

  if (isLoading) return <Loading />;

  return (
    <>
      <AlertModal
        isOpen={category.isDeleteModalOpen}
        onClose={() =>
          dispatch({
            type: ActionTypes.SET_IS_DELETE_MODAL_OPEN,
            payload: false
          })
        }
        isLoading={isPending}
        onConfirm={async () => {
          mutate(category?.category?.id);
        }}
        title="آیا از حذف دسته بندی اطمینان دارید؟"
        description="این عملیات قابل برگشت نخواهد بود و دسته بندی بصورت دائمی حذف خواهد شد"
      />

      <CategoryModal
        isOpen={category.isModalOpen}
        onClose={() =>
          dispatch({ type: ActionTypes.SET_IS_MODAL_OPEN, payload: false })
        }
        category={category.category}
      />

      <div className="flex items-center px-5 py-2">
        <button
          className="flex items-center gap-x-1 text-sm hover:text-primary"
          onClick={() => {
            dispatch({ type: ActionTypes.SET_CATEGORY, payload: null });
            dispatch({ type: ActionTypes.SET_IS_MODAL_OPEN, payload: true });
          }}
        >
          <XSquareIcon size={14} className="text-primary" />
          ساخت دسته بندی جدید
        </button>

        <TableSearch className="ms-auto h-8" />
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ردیف</TableHead>
            <TableHead>نام</TableHead>
            <TableHead>توضیحات</TableHead>
            <TableHead>ویرایش</TableHead>
            <TableHead>حذف</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {categories?.data.map((categoryItem, index) => (
            <TableRow key={index}>
              <TableCell>{index + 1}</TableCell>
              <TableCell>{categoryItem.name}</TableCell>
              <TableCell>{categoryItem.description}</TableCell>
              <TableCell>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch({
                      type: ActionTypes.SET_CATEGORY,
                      payload: categoryItem
                    });
                    dispatch({
                      type: ActionTypes.SET_IS_MODAL_OPEN,
                      payload: true
                    });
                  }}
                >
                  <PencilIcon className="text-slate-700" />
                </button>
              </TableCell>
              <TableCell>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch({
                      type: ActionTypes.SET_IS_DELETE_MODAL_OPEN,
                      payload: true
                    });
                    dispatch({
                      type: ActionTypes.SET_CATEGORY,
                      payload: categoryItem
                    });
                  }}
                >
                  <Trash2Icon className="text-red-500" />
                </button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!categories?.data?.length && <EmptyState />}
      <TablePagination totalItems={categories?.totalCount ?? 0} />
    </>
  );
};

const Loading = () => (
  <div className="flex h-[calc(100vh/2)] w-full items-center justify-center">
    <MoonLoader color="#0099A5" size={50} />
  </div>
);

const EmptyState = () => (
  <div className="flex h-32 w-full items-center justify-center bg-white shadow-md">
    <p className="text-slate-500">دسته بندی پیدا نشد</p>
  </div>
);

export default CategoriesPage;
